/**
 * Shared utilities for the decomposed SharedComponentRenderer renderers.
 * All helpers in this file are consumed by two or more renderer files.
 */
import React from "react";
import type { Component, Viewport } from "@/types";

// ─────────────────────────────────────────────────────────────────────────────
// Base prop interface shared by all renderer files
// ─────────────────────────────────────────────────────────────────────────────

export interface RendererBaseProps {
  component: Component;
  viewport?: Viewport;
  allComponents?: Component[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Child-resolution helper (replaces all 11 Group-A `as any` casts)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the rendered children for a container component.
 *
 * Priority:
 *  1. `component._childComponents` — pre-resolved Component[] injected by canvas pipelines.
 *  2. `component.children` (string IDs) → lookup in allComponents.
 *  3. allComponents filtered by parentId.
 *  4. Empty array.
 */
export function resolveChildComponents(
  component: Component,
  allComponents: Component[] | undefined,
): Component[] {
  if (component._childComponents && component._childComponents.length > 0) {
    return component._childComponents;
  }
  if (allComponents) {
    const byId = component.children
      .map((childId) => allComponents.find((c) => c.id === childId))
      .filter((c): c is Component => c !== undefined);
    if (byId.length > 0) return byId;
    return allComponents.filter((c) => c.parentId === component.id);
  }
  return [];
}

// ─────────────────────────────────────────────────────────────────────────────
// Viewer-mode detection helper (replaces repeated 10-line inline check)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true when the page is in viewer mode, OR when the active viewport
 * is mobile/tablet (which hides editor chrome to match device rendering).
 */
export function resolveViewerMode(viewport: Viewport | undefined): boolean {
  if (typeof window === "undefined") return false;
  if (window.location.pathname.includes("/viewer/")) return true;
  if (!viewport) return false;
  // Import here to avoid circular deps — Viewport enum values are stable strings.
  return viewport === "mobile" || viewport === "tablet";
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS property narrowing (replaces Group-C `as any` casts)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Narrows an unknown value to a valid React.CSSProperties value for key K.
 *
 * The `value as NonNullable<React.CSSProperties[K]>` assertion is intentional
 * and sound: TypeScript cannot statically prove that a Set<string> member
 * satisfies the specific union type for CSS property K, but the Set.has() call
 * provides runtime proof that the string is one of the known-valid values.
 * This is the narrowest available solution — replacing it with `as any` or
 * removing the assertion would be strictly worse.
 * See .claude/skills/debug/references/forbidden-patterns.md "Sound Alternatives".
 */
export function toCssProperty<K extends keyof React.CSSProperties>(
  value: unknown,
  validValues: ReadonlySet<string>,
  fallback: NonNullable<React.CSSProperties[K]>,
): NonNullable<React.CSSProperties[K]> {
  return typeof value === "string" && validValues.has(value)
    ? (value as NonNullable<React.CSSProperties[K]>) // runtime-validated: Set membership proves this is safe
    : fallback;
}

export const OBJECT_FIT_VALUES = new Set([
  "fill", "contain", "cover", "none", "scale-down",
]);

export const JUSTIFY_CONTENT_VALUES = new Set([
  "flex-start", "flex-end", "center",
  "space-between", "space-around", "space-evenly", "normal",
]);

export const ALIGN_ITEMS_VALUES = new Set([
  "flex-start", "flex-end", "center", "stretch", "baseline", "normal",
]);

// ─────────────────────────────────────────────────────────────────────────────
// HTML input type narrowing (replaces Group-C cast at line 4404)
// ─────────────────────────────────────────────────────────────────────────────

export type HtmlInputType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "url"
  | "search"
  | "date"
  | "time"
  | "datetime-local";

export const HTML_INPUT_TYPES = new Set<string>([
  "text", "email", "password", "number", "tel",
  "url", "search", "date", "time", "datetime-local",
]);

/**
 * Returns a validated HTML input type string, falling back to "text" for
 * unknown values. Replaces `properties.type as any` at the call site.
 */
export function toHtmlInputType(value: unknown): HtmlInputType {
  return typeof value === "string" && HTML_INPUT_TYPES.has(value)
    ? (value as HtmlInputType) // runtime-validated: Set membership proves this is safe
    : "text";
}

// ─────────────────────────────────────────────────────────────────────────────
// Sensor action narrowing (replaces Group-E `as any` at line 3190)
// ─────────────────────────────────────────────────────────────────────────────

export type SensorAction = "control" | "read" | "write";

const SENSOR_ACTIONS = new Set<string>(["control", "read", "write"]);

/**
 * The `value as SensorAction` assertion is intentional and sound: Set.has()
 * provides runtime proof that the string is one of the three valid action
 * values. TypeScript cannot statically verify Set membership against a string
 * literal union, making a runtime-validated assertion the narrowest solution.
 */
export function toSensorAction(value: unknown): SensorAction {
  return typeof value === "string" && SENSOR_ACTIONS.has(value)
    ? (value as SensorAction) // runtime-validated: Set membership proves this is safe
    : "control";
}

// ─────────────────────────────────────────────────────────────────────────────
// Sensor control parameters (replaces Group-E `Record<string, any>` at 3191)
// ─────────────────────────────────────────────────────────────────────────────

export interface SensorControlParameters {
  [key: string]: string | number | boolean;
}

/**
 * Converts an unknown data object to typed sensor control parameters by
 * filtering out non-primitive values. Replaces `data.parameters as Record<string, any>`.
 */
export function toSensorParameters(data: unknown): SensorControlParameters {
  if (typeof data !== "object" || data === null) return {};
  return Object.fromEntries(
    Object.entries(data as Record<string, unknown>).filter(
      ([, v]) =>
        typeof v === "string" || typeof v === "number" || typeof v === "boolean",
    ),
  ) as SensorControlParameters;
}

// ─────────────────────────────────────────────────────────────────────────────
// Breakpoint style property accessor (replaces Group-C cast at line 123)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Safely accesses a dynamic property from a breakpoint style record.
 * Returns `unknown` to force proper narrowing at each call site — the
 * values originate from AI-generated JSON and cannot be statically typed.
 * Replaces `(breakpointStyle as any)?.[propName]`.
 */
export function getBreakpointStyleProperty(
  breakpointStyle: Record<string, unknown> | undefined,
  propName: string,
): unknown {
  return breakpointStyle?.[propName];
}
